import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Section, Field, Toggle, StatusBadge, STATUS_META, errToast } from "@/components/tax/shared";

const useQ = (key, url) => useQuery({ queryKey: key, queryFn: async () => (await api.get(url)).data });

export function OverviewPanel() {
  const { data } = useQ(["admin-tax-overview"], "/admin/tax/overview");
  if (!data) return <p className="text-slate-500">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="admin-tax-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(data.counts).map(([s, n]) => (
          <div key={s} className="tax-card p-4" data-testid={`admin-tax-count-${s}`}><p className="text-2xl font-black">{n}</p><p className="text-xs text-slate-500">{STATUS_META[s].label}</p></div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <div className="tax-card p-4"><p className="text-2xl font-black">{data.total}</p><p className="text-xs text-slate-500">Active returns</p></div>
        <div className="tax-card p-4"><p className="text-2xl font-black">{data.preparers}</p><p className="text-xs text-slate-500">Tax preparers</p></div>
        <div className="tax-card p-4"><p className={`text-2xl font-black ${data.open_errors ? "text-red-600" : ""}`}>{data.open_errors}</p><p className="text-xs text-slate-500">Open errors</p></div>
      </div>
    </div>
  );
}

function RulesEditor({ year, onSaved }) {
  const { data } = useQ(["admin-tax-rules", year], `/admin/tax/rules/${year}`);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [tests, setTests] = useState(null);
  useEffect(() => { if (data) setText(JSON.stringify(data.rules, null, 2)); }, [data]);
  const parse = () => { try { return JSON.parse(text); } catch { toast.error("Rules must be valid JSON."); return null; } };
  const test = async () => {
    const rules = parse(); if (!rules) return;
    try { const r = (await api.post(`/admin/tax/rules/${year}/test`, { rules })).data; setTests(r); } catch (e) { errToast(e); }
  };
  const save = async () => {
    const rules = parse(); if (!rules) return;
    try { const r = (await api.post(`/admin/tax/rules/${year}`, { rules, note })).data; toast.success(`Saved as version ${r.version} (not active until you activate it)`); setTests({ valid: true, tests: r.tests }); onSaved(); } catch (e) { errToast(e); }
  };
  return (
    <div className="mt-4" data-testid={`admin-tax-rules-editor-${year}`}>
      <textarea data-testid="admin-tax-rules-json" value={text} onChange={(e) => setText(e.target.value)} rows={18} spellCheck={false} className="w-full font-mono text-xs rounded-md border border-input p-3" />
      <div className="flex flex-wrap gap-2 items-center mt-2">
        <input data-testid="admin-tax-rules-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Change note (e.g. CRA indexation update)" className="h-9 flex-1 min-w-[220px] rounded-md border border-input px-3 text-sm" />
        <Button variant="outline" size="sm" data-testid="admin-tax-rules-test-btn" onClick={test}>Validate & test</Button>
        <Button size="sm" data-testid="admin-tax-rules-save-btn" className="bg-red-600 hover:bg-red-700 text-white" onClick={save}>Save new version</Button>
      </div>
      {tests && (
        <div className="mt-3 text-sm space-y-1" data-testid="admin-tax-rules-test-results">
          {tests.errors?.map((e) => <p key={e} className="text-red-600">✗ {e}</p>)}
          {tests.tests?.map((t) => <p key={t.name} className={t.passed ? "text-emerald-700" : "text-red-600"}>{t.passed ? "✓" : "✗"} {t.name} <span className="text-slate-400">({t.detail})</span></p>)}
        </div>
      )}
    </div>
  );
}

export function YearsPanel() {
  const qc = useQueryClient();
  const { data: years = [] } = useQ(["admin-tax-years"], "/admin/tax/years");
  const [open, setOpen] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-tax-years"] });
  const upd = async (y, body) => { try { await api.put(`/admin/tax/years/${y}`, body); toast.success("Updated"); refresh(); } catch (e) { errToast(e); } };
  return (
    <div className="space-y-4" data-testid="admin-tax-years">
      {years.map((y) => (
        <Section key={y.year} title={`${y.year} tax year`} subtitle={`${y.returns} return(s) · active rules v${y.active_version}`}
          right={<div className="flex items-center gap-2">
            <Toggle id={`admin-tax-year-enabled-${y.year}`} label="Enabled" checked={y.enabled} onChange={(v) => upd(y.year, { enabled: v })} />
            <Button size="sm" variant="outline" data-testid={`admin-tax-year-rules-btn-${y.year}`} onClick={() => setOpen(open === y.year ? null : y.year)}>{open === y.year ? "Close rules" : "Edit rules"}</Button>
          </div>}>
          <div className="flex flex-wrap gap-2 text-xs">
            {y.versions.map((v) => (
              <button key={v.version} data-testid={`admin-tax-activate-${y.year}-${v.version}`} disabled={v.version === y.active_version}
                onClick={() => upd(y.year, { active_version: v.version })}
                className={`px-3 py-1.5 rounded-full border ${v.version === y.active_version ? "bg-red-600 text-white border-red-600" : "bg-white hover:border-red-400"}`}>
                v{v.version} · {v.note || "—"} {v.version === y.active_version ? "(active)" : "· activate"}
              </button>
            ))}
          </div>
          {open === y.year && <RulesEditor year={y.year} onSaved={refresh} />}
        </Section>
      ))}
    </div>
  );
}

export function SettingsPanel() {
  const qc = useQueryClient();
  const { data } = useQ(["admin-tax-settings"], "/admin/tax/settings");
  const [s, setS] = useState(null);
  useEffect(() => { if (data) setS(data); }, [data]);
  if (!s) return <p className="text-slate-500">Loading…</p>;
  const save = async () => {
    try { await api.put("/admin/tax/settings", { ...s, retention_years: Number(s.retention_years), max_upload_mb: Number(s.max_upload_mb) }); toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["admin-tax-settings"] }); } catch (e) { errToast(e); }
  };
  const purge = async () => {
    if (!window.confirm("Permanently crypto-shred all returns older than the retention period?")) return;
    try { const r = (await api.post("/admin/tax/purge-expired")).data; toast.success(`Purged ${r.purged} return(s) before ${r.cutoff_year}`); } catch (e) { errToast(e); }
  };
  return (
    <Section title="Workflow, retention & security" testId="admin-tax-settings">
      <div className="grid md:grid-cols-2 gap-4">
        <Field id="admin-tax-retention" label="Retention (years, CRA minimum 6)" type="number" value={s.retention_years} onChange={(e) => setS({ ...s, retention_years: e.target.value })} />
        <Field id="admin-tax-max-upload" label="Max upload size (MB)" type="number" value={s.max_upload_mb} onChange={(e) => setS({ ...s, max_upload_mb: e.target.value })} />
      </div>
      <Toggle id="admin-tax-intake-open" label="Accept new tax returns" checked={s.intake_open} onChange={(v) => setS({ ...s, intake_open: v })} />
      <Toggle id="admin-tax-ai-enabled" label="AI document reading (Gemini) — values always require customer verification" checked={s.ai_extraction_enabled} onChange={(v) => setS({ ...s, ai_extraction_enabled: v })} />
      <div className="flex justify-between mt-4">
        <Button variant="ghost" className="text-red-600" data-testid="admin-tax-purge-btn" onClick={purge}>Purge expired returns</Button>
        <Button data-testid="admin-tax-settings-save" className="bg-red-600 hover:bg-red-700 text-white rounded-full" onClick={save}>Save settings</Button>
      </div>
    </Section>
  );
}

export function CasesPanel() {
  const [status, setStatus] = useState("");
  const { data: cases = [] } = useQuery({ queryKey: ["admin-tax-cases", status], queryFn: async () => (await api.get("/admin/tax/cases", { params: status ? { status } : {} })).data });
  return (
    <Section title="Customer cases" subtitle="Metadata only — admins cannot view SINs or documents without customer consent." testId="admin-tax-cases"
      right={<select data-testid="admin-tax-cases-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-input px-2 text-sm">
        <option value="">All statuses</option>{Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="py-2">Customer</th><th>Year</th><th>Status</th><th>Preparer</th><th>Docs</th><th>Flags</th><th>Updated</th></tr></thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`admin-tax-case-${c.id}`}>
                <td className="py-2">{c.customer_email}</td><td>{c.tax_year}</td><td><StatusBadge status={c.status} /></td>
                <td>{c.preparer?.name || "—"}</td><td>{c.documents}</td><td>{c.flags}</td><td className="text-slate-500">{c.updated_at ? new Date(c.updated_at).toLocaleDateString() : ""}</td>
              </tr>
            ))}
            {cases.length === 0 && <tr><td colSpan={7} className="py-4 text-slate-500">No cases.</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
